import MenuItem from "@mui/material/MenuItem";
import React, { ReactNode, useState } from "react";
import { ReuseButton, StyledMenu } from "../ui";
import { TriangleIcon } from "../images";
import {
  ButtonsColorType,
  CampaignPurpose,
  GenerateReportsModals,
  Integration,
} from "../../types/enums";
import { useGetCampaignSummaryQuery } from "../../api";
import { useParams } from "react-router-dom";
import { TargetAccountPlansBtn } from "../ui/buttons";
import { CircularProgress } from "@mui/material";
import { useAppSelector } from "../../hooks";
import { selectPermissions } from "../../features/permissionsSlice";
import { ManagePlaybooksModal } from "../ui/modal";

export const GenerateReportsMenu = () => {
  const { campaign_id } = useParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentModal, setCurrentModal] = useState<GenerateReportsModals>(
    GenerateReportsModals.none,
  );

  const { isAllowedEditGenerateReports } = useAppSelector(selectPermissions);

  const { data } = useGetCampaignSummaryQuery({
    campaign_id: campaign_id!,
  });

  const isSalesCampaign = data?.campaign_purpose === CampaignPurpose.sales;

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentModal(GenerateReportsModals.none);
  };

  const openModalHandler = (modal: GenerateReportsModals) => {
    setCurrentModal(modal);
  };

  const options = [
    {
      label: GenerateReportsModals.account_plan,
      isDisabled: !data?.has_accounts_without_plan,
    },
    {
      label: GenerateReportsModals.playbook,
      isDisabled: !data?.has_accounts_without_playbook && !data?.has_playbooks,
    },
  ];

  const modals: Record<GenerateReportsModals, ReactNode> = {
    [GenerateReportsModals.account_plan]: (
      <TargetAccountPlansBtn
        campaign_id={campaign_id!}
        onClose={handleClose}
        hideBtn={true}
        setIsLoading={setIsLoading}
      />
    ),
    [GenerateReportsModals.playbook]: (
      <ManagePlaybooksModal
        isOpen={true}
        onClose={handleClose}
        setIsLoading={setIsLoading}
        withGenerate={true}
        isAllowedRemove={!!data?.has_playbooks}
        isAllowedGenerate={!!data?.has_accounts_without_playbook}
        shouldHaveHGData={
          !!data?.crm && data.crm.integration === Integration.lead_tools
        }
      />
    ),
    [GenerateReportsModals.none]: null,
  };

  return (
    <div>
      <ReuseButton
        onClick={handleClick}
        endIcon={
          <div style={{ width: "18px" }}>
            <TriangleIcon
              color={
                !isAllowedEditGenerateReports || isSalesCampaign
                  ? "grey"
                  : "#BD4D00"
              }
            />
          </div>
        }
        buttonColor={ButtonsColorType.dark_orange}
        disabled={!isAllowedEditGenerateReports || isSalesCampaign}
      >
        Targeted Reports
      </ReuseButton>

      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "white",
          },
        }}
      >
        {options.map(({ label, isDisabled }) => (
          <MenuItem
            key={label}
            sx={{ display: "flex", gap: "12px", alignItems: "center" }}
            onClick={() => openModalHandler(label)}
            disabled={isDisabled || isLoading}
          >
            {label}
            {isLoading && label === currentModal && (
              <CircularProgress size={20} color="inherit" />
            )}
          </MenuItem>
        ))}
      </StyledMenu>
      {modals[currentModal]}
    </div>
  );
};
